import React, { useEffect, useRef, useState } from 'react';

import { Button, IconButton, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import { MdClose } from 'react-icons/md';

import { usePermissionsContext } from '../../../context/permissionsContext';
import { track } from '../../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../../services/analytics/events';
import { useQueueOnboardingSteps } from '../steps/useQueueOnboardingSteps';
import { runTourAction } from '../tourActions';
import {
  useOnboardingDismissalStore,
  useShouldShowOnboarding,
} from '../useOnboardingDismissalStore';

const MOUNT_DELAY_MS = 800;
const ONBOARDING_KEY = 'queue';
// This launcher renders OUTSIDE <RouterProvider> (OnboardingRoot is a sibling of
// the router), so it cannot call useNavigate. "Visit the FAQ" runs a tour action
// the in-router queue page (Search.tsx) registers with navigate().
const VISIT_FAQ_ACTION = 'queue:visit-faq';

const EVENT_PROPS = {
  platform_page: 'Upcoming ratings',
  feature_name: 'ratings-queue',
  tutorial_screen_header_text: 'Upcoming ratings tour',
};

/**
 * Ratings-queue onboarding = a plain CENTERED Joy Modal two-step walkthrough
 * (Figma 567:124211 / 465:81464), NOT a driver.js coach-mark. A Joy Modal
 * centres natively, owns its dim+blur backdrop, keeps the close X correctly
 * placed, and — because this component renders inside <RouterProvider> — lets
 * "Visit the FAQ" navigate directly. Mounted by OnboardingRoot on
 * `activeOnboardingKey === 'queue'`; opens on page load (dev: every visit,
 * prod: once) unless dismissed/completed.
 */
export const QueueOnboardingLauncher: React.FC = () => {
  const { userData } = usePermissionsContext();
  const steps = useQueueOnboardingSteps(userData?.benefits?.fastpass ?? null);
  // Honours the persisted (localStorage) dismissal in ALL envs — once the user
  // closes it (cross or "Get started"), it never reappears. To re-test locally,
  // clear the onboarding-dismissal key from localStorage.
  const shouldShow = useShouldShowOnboarding(ONBOARDING_KEY);
  const markCompleted = useOnboardingDismissalStore((s) => s.markCompleted);

  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!shouldShow || startedRef.current) return;
    const timer = window.setTimeout(() => {
      if (startedRef.current) return;
      startedRef.current = true;
      track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, {
        ...EVENT_PROPS,
        tutorial_step_number: '1',
      });
      setIdx(0);
      setOpen(true);
    }, MOUNT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShow]);

  if (!steps.length) return null;
  const step = steps[Math.min(idx, steps.length - 1)];
  const isLast = idx >= steps.length - 1;
  const primaryText = step.primaryLabel ?? (isLast ? 'Get started' : 'Next');

  const close = () => {
    // Permanently dismiss on the cross OR on finishing the last step — persisted
    // to localStorage (markCompleted), so it never reappears for this user.
    markCompleted(ONBOARDING_KEY);
    setOpen(false);
  };
  const next = () => {
    track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
      ...EVENT_PROPS,
      tutorial_step_number: String(idx + 1),
      button_text: primaryText,
    });
    if (isLast) close();
    else setIdx((i) => i + 1);
  };
  const visitFaq = () => {
    track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
      ...EVENT_PROPS,
      tutorial_step_number: String(idx + 1),
      button_text: 'Visit the FAQ',
    });
    close();
    // Bridge to the in-router page (Search.tsx registers this action -> navigate).
    runTourAction(VISIT_FAQ_ACTION);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      // Figma 363:46536 backdrop: #121416 @ 0.25 + blur(4).
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(18, 20, 22, 0.25)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <ModalDialog
        variant='plain'
        sx={(t) => ({
          position: 'relative',
          width: 612, // root frame 567:124211 / 465:81464
          maxWidth: '94vw',
          p: '24px 40px', // pad T24 / R40 / B24 / L40
          gap: '24px', // root itemSpacing
          borderRadius: '8px',
          border: '1px solid',
          borderColor: t.palette.mode === 'dark' ? '#333333' : '#E5E5E5',
          bgcolor: t.palette.background.surface,
          boxShadow: 'lg',
        })}
      >
        {/* Close X — a FLOW child in its own top row (Figma 567:124212 is
            layoutPositioning AUTO, 40x40, right-aligned), NOT an absolute overlay,
            so it sits ABOVE the preview with a 24px gap instead of over it. */}
        <IconButton
          variant='plain'
          onClick={close}
          aria-label='Close tour'
          sx={(t) => ({
            alignSelf: 'flex-end',
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            p: 0,
            borderRadius: '6px',
            color: t.palette.text.icon,
          })}
        >
          <MdClose size={24} />
        </IconButton>

        {/* Preview (imaged Figma node). */}
        {step.customContent}

        {/* Title — 28/700/40, centered. */}
        <Typography
          sx={(t) => ({
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '40px',
            textAlign: 'center',
            color: t.palette.text.primary,
          })}
        >
          {step.title}
        </Typography>

        {/* Body — 18/400/27.9, centered. */}
        <Typography
          sx={(t) => ({
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '27.9px',
            textAlign: 'center',
            color: t.palette.mode === 'dark' ? '#CCCCCC' : '#4D4D4D',
          })}
        >
          {step.body}
        </Typography>

        {/* Buttons — body->buttons gap is 40 (= 24 base gap + 16 mt). Two 48-tall,
            radius-6 buttons, gap 20, each filling half the 532 content width. */}
        <Stack direction='row' gap='20px' sx={{ mt: '16px' }}>
          <Button
            variant='outlined'
            color='neutral'
            onClick={visitFaq}
            sx={{ flex: 1, minHeight: 48, borderRadius: '6px', fontSize: '16px', fontWeight: 400 }}
          >
            Visit the FAQ
          </Button>
          <Button
            variant='solid'
            color='primary'
            onClick={next}
            sx={{ flex: 1, minHeight: 48, borderRadius: '6px', fontSize: '16px', fontWeight: 400 }}
          >
            {primaryText}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default QueueOnboardingLauncher;
